import React, { createContext, useEffect, useReducer } from 'react';
import jwtDecode from 'jwt-decode';
import SplashScreen from 'src/components/SplashScreen';
import axios from 'src/utils/axios';

const paths = {
  LOGIN: 'account/login/',
  REGISTER: 'account/register'
  // SIGNOUT: 'account/signout'
};

const initialAuthState = {
  isAuthenticated: false,
  isInitialised: false,
  isMentor: false,
  isStudent: false,
  isOps: false,
  isAdmin: false,
  user: null,
  userType: null
};

const isValidToken = accessToken => {
  if (!accessToken) {
    return false;
  }

  const decoded = jwtDecode(accessToken);
  const currentTime = Date.now() / 1000;

  return decoded.exp > currentTime;
};

const setSession = accessToken => {
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem('accessToken');
    delete axios.defaults.headers.common.Authorization;
  }
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'INITIALISE': {
      const { isAuthenticated, user } = action.payload;
      // setupUserInstance(user);
      const { isUsersObject, userType } = getUserType(user);
      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user,
        userType,
        ...isUsersObject
      };
    }
    case 'LOGIN': {
      const { user } = action.payload;
      setupUserInstance(user);
      const { isUsersObject, userType } = getUserType(user);

      return {
        ...state,
        isAuthenticated: true,
        user,
        userType,
        ...isUsersObject
      };
    }
    case 'LOGOUT': {
      return {
        ...state,
        isAuthenticated: false,
        user: null
      };
    }
    case 'REGISTER': {
      const { user } = action.payload;
      setupUserInstance(user);
      const { isUsersObject, userType } = getUserType(user);

      return {
        ...state,
        isAuthenticated: true,
        user,
        userType,
        ...isUsersObject
      };
    }
    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext({
  ...initialAuthState,
  method: 'JWT',
  login: () => Promise.resolve(),
  logout: () => {},
  register: () => Promise.resolve()
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialAuthState);

  const login = async (email, password) => {
    const response = await axios.post(paths.LOGIN, {
      username: email,
      password
    });
    const { accessToken, user } = response.data;

    setupUserInstance(user);
    setSession(accessToken);
    dispatch({
      type: 'LOGIN',
      payload: {
        user
      }
    });
  };

  const logout = () => {
    setSession(null);
    dispatch({ type: 'LOGOUT' });
  };

  const register = async (email, name, password, userType) => {
    const response = await axios.post(paths.REGISTER, {
      username: email,
      name,
      password,
      userType
    });

    const { accessToken, user } = response.data;
    window.localStorage.setItem('accessToken', accessToken);

    dispatch({
      type: 'REGISTER',
      payload: {
        user,
        userType
      }
    });
  };

  useEffect(() => {
    const initialise = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');

        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken);

          const response = await axios.get('/account/me');
          const { user } = response.data;

          setupUserInstance(user);

          dispatch({
            type: 'INITIALISE',
            payload: {
              isAuthenticated: true,
              user
            }
          });
        } else {
          dispatch({
            type: 'INITIALISE',
            payload: {
              isAuthenticated: false,
              user: null
            }
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALISE',
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
      }
    };

    initialise();
  }, []);

  if (!state.isInitialised) {
    return <SplashScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'JWT',
        login,
        logout,
        register
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
   This function will take user and return 2 objects
   1. `isUsersObject`: an object that will return 4 questions 
   ```
   {
     isMentor, 
     isStudent, 
     isOps, 
     isAdmin
   }
    ```
   2. `userType`: a single string value that will return a single permission for the user. 
   if the user was both mentor and student, `userType` would be only mentor
*/
const getUserType = user => {
  let users = {
    isMentor: false,
    isStudent: false,
    isOps: false,
    isAdmin: false
  };
  let userType = null;

  if (user?.profile?.mentor) {
    users.isMentor = true;
    userType = 'mentor';
  }
  if (user?.profile?.student) {
    users.isStudent = true;
    userType = 'student';
  }
  if (user?.profile?.admin) {
    users.isAdmin = true;
    userType = 'admin';
  }
  if (user?.profile?.ops) {
    users.isOps = true;
    userType = 'ops';
  }
  return { isUsersObject: users, userType };
};

/**
 *
 * This function will faltten avatar and  name of the user
 */
const setupUserInstance = user => {
  // user.avatar = user.profile.image;
  // user.name = user.profile.name;
  user.profile.email = user.email;
};
export default AuthContext;
